import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';

const parseTerms = (raw) =>
  Array.from(new Set(
    (raw || '')
      .split(/[\s,;]+/)
      .map(s => s.trim())
      .filter(Boolean)
  ));

const BulkSearchModal = ({ isOpen, onClose, onResults, selectedProperties, isDark, authFetch }) => {
  const [idNumbers, setIdNumbers] = useState('');
  const [emails, setEmails] = useState('');
  const [phones, setPhones] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setIdNumbers('');
      setEmails('');
      setPhones('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const parsedIds = parseTerms(idNumbers);
  const parsedEmails = parseTerms(emails);
  const parsedPhones = parseTerms(phones);
  const totalTerms = parsedIds.length + parsedEmails.length + parsedPhones.length;
  const canSubmit = totalTerms > 0 && !loading;

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/bulk-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idNumbers: parsedIds,
          emails: parsedEmails,
          phones: parsedPhones,
          properties: selectedProperties,
        }),
      });
      if (!response.ok) {
        throw new Error(response.status === 429 ? 'Rate limited, try again shortly.' : 'Bulk search failed.');
      }
      const data = await response.json();
      onResults(data);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `text-sm font-semibold mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`;
  const textareaCls = `w-full h-40 p-2 rounded-lg border text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-brand-200
    ${isDark
      ? 'bg-night-header border-brand-800 text-white placeholder-gray-400 focus:border-brand-500'
      : 'bg-white border-brand-200 text-gray-900 placeholder-gray-400 focus:border-brand-500'}`;
  const countCls = `text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <div
      className="fixed inset-0 bg-night bg-opacity-70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col rounded-lg
          ${isDark ? 'bg-night-raised' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`p-4 border-b flex justify-between items-center
          ${isDark ? 'bg-night-header border-brand-900 text-brand-200' : 'bg-brand-50 border-brand-100 text-brand-900'}`}>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Bulk Search
          </h2>
          <button
            onClick={onClose}
            className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 overflow-auto">
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Paste lists into any of the three fields below (one value per line, or separated by spaces/commas).
            Results use your current column selection.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label className={labelCls}>ID Numbers</label>
              <textarea
                value={idNumbers}
                onChange={e => setIdNumbers(e.target.value)}
                className={textareaCls}
                placeholder="123456789&#10;987654321"
              />
              <span className={countCls}>{parsedIds.length} entries</span>
            </div>

            <div className="flex flex-col">
              <label className={labelCls}>Emails</label>
              <textarea
                value={emails}
                onChange={e => setEmails(e.target.value)}
                className={textareaCls}
                placeholder="alice@example.com&#10;bob@example.com"
              />
              <span className={countCls}>{parsedEmails.length} entries</span>
            </div>

            <div className="flex flex-col">
              <label className={labelCls}>Phones</label>
              <textarea
                value={phones}
                onChange={e => setPhones(e.target.value)}
                className={textareaCls}
                placeholder="+972-50-1234567&#10;0521234567"
              />
              <span className={countCls}>{parsedPhones.length} entries</span>
            </div>
          </div>

          {error && (
            <div className={`mt-4 p-3 rounded-lg text-sm
              ${isDark ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-700'}`}>
              {error}
            </div>
          )}
        </div>

        <div className={`p-4 border-t flex justify-between items-center
          ${isDark ? 'border-brand-900' : 'border-brand-100'}`}>
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {totalTerms} total {totalTerms === 1 ? 'term' : 'terms'}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 border rounded-lg
                ${isDark
                  ? 'border-brand-800 text-brand-200 hover:bg-night-header'
                  : 'border-brand-200 text-brand-800 hover:bg-brand-50'}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:bg-brand-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSearchModal;
